<template>
  <Container>
    <Card :class="$style.card">
      <div
        v-if="minTitle"
        :class="$style.minTitleBlock"
      >
        <HeadingExtraSmall>Update History</HeadingExtraSmall>
        <ButtonLink
          text="View Activity Log"
          @click="handleActivityLogLinkClick"
        />
      </div>
      <div
        v-else
        :class="$style.titleBlock"
      >
        <HeadingSmall>Event Frequency</HeadingSmall>
      </div>
      <div :class="$style.graphContainer">
        <LoadingOverlay
          :showLoadingOverlay="isLoading"
        />
        <div :class="$style.dropdowns">
          <DropDownMultiSelectSearch
            alwaysShowSearch
            :class="$style.dropdown"
            emptyValueText="All"
            hideShowingResults
            name="Event Type"
            :options="eventTypeOptions"
            showFullValueText
            :value="appliedFilters"
            @input="dropdownAction"
          />
          <DropDownMultiSelectSearch
            v-if="showOsFilter"
            alwaysShowSearch
            :class="$style.dropdown"
            emptyValueText="All"
            hideShowingResults
            name="OS"
            :options="availableOsFilters"
            showFullValueText
            :value="appliedOsFilters"
            @input="osDropdownAction"
          />
          <SystemFleetDistributionDatePicker
            v-if="showTimeFrame"
            :defaultTimeFrame="defaultTimeFrame"
            @change="handleQuickPickClick"
          />
        </div>
        <ChartWrapper
          :class="$style.chartWrapper"
          :options="options"
          :series="handleChart"
          type="bar"
        />
        <HeadingExtraSmall
          :class="$style.timeRange"
          element="h5"
          weight="bold"
        >
          Time Range by {{ timeRange }}
        </HeadingExtraSmall>
      </div>
    </Card>
  </Container>
</template>

<script>
import {
  ButtonLink,
  Container,
  HeadingExtraSmall,
  HeadingSmall,
} from '@jumpcloud/ui-components';
import {
  ChartWrapper,
} from '@jumpcloud/ui-components/charts';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { mapActions, mapGetters } from 'vuex';
import {
  quickPicksMetadata,
  quickPicksOptions,
} from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import Card from 'components/Card.vue';
import Constants, {
  ItemTypeConsts, StoreModuleMap,
} from 'client/util/Constants';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch';
import InsightsEventsApi from 'client/api/v2/InsightsEventsApi';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import SystemFleetDistributionDatePicker from 'client/systems/SystemFleetDistributionDatePicker';
import eventTypes, { eventTypeNameMap } from 'client/Insights/ActivityLog/constants/EventTypes';
import moment from 'moment';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];

const {
  linux,
  mac,
  windows,
} = Constants;

const DEFAULT_TIME_FRAME = 7;

export default {
  name: 'OsEventsGraph',

  components: {
    ButtonLink,
    Card,
    Container,
    ChartWrapper,
    DropDownMultiSelectSearch,
    HeadingExtraSmall,
    HeadingSmall,
    LoadingOverlay,
    SystemFleetDistributionDatePicker,
  },

  props: {
    customChartHeight: {
      type: Number,
      default: 270,
    },
    minTitle: {
      type: Boolean,
      default: false,
    },
    showOsFilter: {
      type: Boolean,
      default: false,
    },
    showTimeFrame: {
      type: Boolean,
      default: false,
    },
    timeFrame: {
      type: Number,
      default: 7,
    },
  },

  data: () => ({
    quickPicksMetadata,
    appliedFilters: [],
    appliedOsFilters: [],
    availableEventsFilters: [],
    availableOsFilters: [],
    defaultTimeFrame: quickPicksMetadata[quickPicksOptions.sevenDays],
    defaultOSes: ['darwin', linux.toLowerCase(), windows.toLowerCase()],
    timeRangeOptions: [
      {
        time: 1,
        text: 'Day',
        unit: 'd',
      },
      {
        time: 3,
        text: '3 Days',
        unit: 'd',
      },
      {
        time: 7,
        text: 'Week',
        unit: 'w',
      },
    ],
    timeFrameSelected: DEFAULT_TIME_FRAME,
    // Default value 1 day
    eventsGroupingIntervalDays: 1,
    eventsList: [],
    isLoading: true,
  }),

  computed: {
    ...mapGetters(insightsEventModule, ['getEventTypeFound']),

    dateFilter() {
      // time frame -> day amount translation map
      const timeFrameTranslations = {
        1: quickPicksOptions.twentyFourHours,
        3: quickPicksOptions.threeDays,
        7: quickPicksOptions.sevenDays,
        15: quickPicksOptions.fifteenDays,
        30: quickPicksOptions.thirtyDays,
        45: quickPicksOptions.fortyFiveDays,
        60: quickPicksOptions.sixtyDays,
        90: quickPicksOptions.ninetyDays,
      };

      // get correct day amount from translation map, using selected time frame
      return timeFrameTranslations[this.timeFrameSelected || DEFAULT_TIME_FRAME];
    },

    defaultEvents() {
      const events = [
        eventTypes.osMinorUpgrade,
        eventTypes.osMajorUpgrade,
        eventTypes.osMinorRollback,
        eventTypes.osMajorRollback,
      ];

      if (!this.showOsFilter) {
        events.unshift(eventTypes.systemDelete, eventTypes.systemCreate);
      }

      return events;
    },

    eventTypeOptions() {
      if (this.showTimeFrame) {
        return this.availableEventsFilters.map((option) => ({
          value: option,
          name: eventTypeNameMap[option],
        })).sort((a, b) => a.name.localeCompare(b.name));
      }
      return this.getEventTypeFound.map((option) => ({
        value: option,
        name: eventTypeNameMap[option],
      })).sort((a, b) => a.name.localeCompare(b.name));
    },

    handleChart() {
      return [{ data: this.eventsList }];
    },

    options() {
      return {
        chart: {
          height: this.customChartHeight,
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        fill: {
          colors: '#71D6D2',
        },
        grid: {
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        noData: {
          text: 'No recent activity for this time frame.',
        },
        plotOptions: {
          bar: {
            borderRadius: 0,
            columnWidth: '22px',
            horizontal: false,
          },
        },
        tooltip: {
          enabled: true,
          custom: ({
            series, seriesIndex, dataPointIndex, w,
          }) => {
            const timeFormat = 'MMM DD, YYYY HH:mm:ss';
            const eventDay = moment(
              new Date(w.config.series[0].data[dataPointIndex].x),
            ).format(timeFormat);
            const nextEventDay = moment(
              new Date(w.config.series[0].data[dataPointIndex].x),
            ).add(this.eventsGroupingIntervalDays, 'days').subtract(1, 'seconds').format(timeFormat);

            return (`<div class="${this.$style.tooltip}"><div class="arrow"></div><p>Start Time <span>${eventDay}</span></p><p>End Time <span>${nextEventDay}</span></p><p>Event Count <span>${series[seriesIndex][dataPointIndex]}</span></p></div>`);
          },
        },
        xaxis: {
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          categories: [],
          labels: {
            show: true,
            formatter: (value, timestamp) => (this.eventsList.length > 0 ? moment(new Date(timestamp)).format('MMMM DD') : ''),
            style: {
              colors: '#626B73',
              fontSize: '12px',
            },
          },
        },
        yaxis: {
          floating: true,
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            show: true,
            offsetY: -10,
            maxWidth: 1,
            offsetX: 23,
            style: {
              colors: ['#626B73'],
              fontSize: '12px',
            },
          },
        },
      };
    },

    timeRange() {
      return this.timeRangeOptions.filter(
        item => item.time === this.eventsGroupingIntervalDays,
      )[0].text;
    },
  },

  watch: {
    async timeFrame() {
      this.isLoading = true;
      this.appliedFilters = [];
      this.timeFrameSelected = this.timeFrame;
      this.setupEventsDates();
    },
  },

  created() {
    this.setupEventsDates();
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'fetchEventsCount',
      'refreshDates',
      'setFilter',
      'setQuickPickDate',
    ]),

    async checkAvailableEventsFilters() {
      this.defaultEvents.forEach(async (eventType) => {
        this.setQuickPickDate(this.dateFilter);
        this.clearFilters();
        this.refreshDates();
        this.setFilter({
          field: eventFields.eventType,
          value: eventType,
        });

        const { data } = await this.fetchEventsCount({ limit: 1, eventType });

        if (data.count > 0) {
          this.availableEventsFilters.push(eventType);
        }
      });
    },

    async checkAvailableOSesFilters(startTime) {
      this.defaultOSes.forEach(async (os) => {
        const filters = [
          {
            event_type: this.appliedFilters.length > 0
              ? this.appliedFilters
              : this.defaultEvents,
          },
        ];
        filters.push({
          'system.osFamily': [os],
        });

        const { data } = await InsightsEventsApi.fetchIntervalCount({
          service: ['all'],
          search_term: {
            and: filters,
          },
          start_time: startTime,
          timezone: moment().format('ZZ'),
          ...this.setEventsGroupInterval(),
        });

        if (data.buckets && data.buckets.length > 0) {
          if (data.buckets.filter(item => item.doc_count > 0).length > 0) {
            this.availableOsFilters.push({
              value: os,
              name: os === 'darwin' ? mac : os,
            });
          }
        }
      });
    },

    dropdownAction(selectedEventTypes) {
      this.appliedFilters = selectedEventTypes;
      this.setupEventsDates();
    },

    handleActivityLogLinkClick() {
      // Clear any existing Activity Log filters
      this.clearFilters();

      // Set the time range
      this.setQuickPickDate(this.dateFilter);

      // Set the Events filter for the Activity Log
      this.setFilter({
        field: eventFields.eventType,
        value: this.appliedFilters.length > 0
          ? this.appliedFilters
          : this.defaultEvents,
      });

      this.$router.push('/insights');
    },

    handleQuickPickClick(quickPick) {
      this.isLoading = true;
      this.appliedFilters = [];
      this.appliedOsFilters = [];
      this.timeFrameSelected = quickPick;
      this.setupEventsDates();
    },

    osDropdownAction(selectedEventTypes) {
      this.appliedOsFilters = selectedEventTypes;
      this.setupEventsDates();
    },

    setEventsGroupInterval() {
      let selectedTimeRange = {};
      if (this.timeFrameSelected <= 7) {
        selectedTimeRange = {
          time: this.timeRangeOptions[0].time,
          unit: this.timeRangeOptions[0].unit,
        };
      } else if (this.timeFrameSelected > 7 && this.timeFrameSelected <= 30) {
        selectedTimeRange = {
          time: this.timeRangeOptions[1].time,
          unit: this.timeRangeOptions[1].unit,
        };
      } else {
        selectedTimeRange = {
          time: this.timeRangeOptions[2].time,
          unit: this.timeRangeOptions[2].unit,
        };
      }

      this.eventsGroupingIntervalDays = selectedTimeRange.time;

      return {
        interval_unit: selectedTimeRange.unit,
        interval_value: selectedTimeRange.unit === 'w' ? '1' : selectedTimeRange.time.toString(),
      };
    },

    async setupEventsDates() {
      this.isLoading = true;
      this.eventsList = [];
      this.availableEventsFilters = [];
      this.availableOsFilters = [];
      const startTime = moment().subtract(this.timeFrameSelected, 'days').toISOString();

      if (this.showOsFilter) {
        // Check which events are available to filter
        await this.checkAvailableEventsFilters();

        // Check which OSes are available to filter
        await this.checkAvailableOSesFilters(startTime);
      }

      const filters = [
        {
          event_type: this.appliedFilters.length > 0
            ? this.appliedFilters
            : this.defaultEvents,
        },
      ];

      if (this.appliedOsFilters.length > 0) {
        filters.push({
          'system.osFamily': this.appliedOsFilters,
        });
      }

      // Get events count by interval
      const { data } = await InsightsEventsApi.fetchIntervalCount({
        service: ['all'],
        search_term: {
          and: filters,
        },
        start_time: startTime,
        timezone: moment().format('ZZ'),
        ...this.setEventsGroupInterval(),
      });

      if (!data.buckets?.length) {
        this.eventsList = [];
      } else {
        // Chart data format
        this.eventsList = data.buckets.reduce((acc, interval) => {
          if (interval.doc_count > 0) {
            const date = new Date(interval.key_as_string).toLocaleDateString('en-us', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            });

            acc.push({ x: date, y: interval.doc_count });
          }
          return acc;
        }, []);
      }

      this.isLoading = false;
    },
  },
};
</script>

<style module>
.card {
  height: 100%;
}

.titleBlock {
  border-bottom: var(--jc-border);
  flex: 0 0 65%;
  margin-bottom: var(--jc-spacer);
  padding-bottom: var(--jc-spacer);
}

.minTitleBlock {
  align-items: baseline;
  display: flex;
  justify-content: space-between;
}

.graphContainer {
  position: relative;
}

.tooltip {
  background: var(--jcGrey600);
  border-radius: 3px;
  height: 11rem;
  padding: 1rem;
  position: relative;
  width: 12rem;
}

.tooltip p {
  color: var(--jcGrey300);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  margin: 0;
  margin-bottom: 1rem;
}

.tooltip span {
  color: var(--jcWhite50);
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.125rem;
}

.dropdowns {
  display: flex;
}

.dropdown {
  margin-right: 0.625rem;
}

.dropdown button {
  background: var(--jcWhite50);
  border: 2px solid var(--jcGrey900);
  border-radius: 4px;
}

.dropdown span:first-child {
  padding-right: 4px;
}

.dropdown svg {
  padding-left: 5px;
}

.chartWrapper {
  margin-left: -0.563rem;
  margin-top: 1.563rem;
  width: 100%;
}

.labels {
  color: var(--jcGrey);
  font-size: 0.75rem;
}

.datePickerContainer {
  display: inline-grid;
  min-width: 11rem;
}

.timeRange {
  text-align: center;
}

</style>
